import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import dillLogoWithText from '../../assets/svg/dill_logo_with_text.svg';
import { getBasicSupplier } from '../../redux/services/buyersService';
import { AppInputField } from '../../components/general/AppInputField/AppInputField';
import {
  RequestedSupplierJobCreate,
  Supplier,
  SupplierJobAddressDetails,
  SupplierJobUpdate,
} from '@dill/dill-shared';
import { AppButton } from '../../components/general/AppButton/AppButton';
import { useLocation, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import JobDetailsForm from './JobDetailsForm';
import {
  ContructionMonitorPermitOption,
  fetchQueuryJobSiteAddresses,
} from '../../redux/services/verifyAddressService';
import { SupplierVerifiedJobFormData } from '../../components/general/AppNewSupplierJobModal/constants';
import stringSimilarity from 'string-similarity';
import VerifyJobForm from './VerifyJobForm';
import { createRequestedSupplierJob } from '../../redux/services/requestJobInformationService';
import JobSavingSuccess from './JobSavingSuccess';
import { AppSubmitEmailModal } from '../../components/general/AppSubmitEmailModal/AppSubmitEmailModal';
import { on } from 'events';
import { AppEditBuyerAddressModal } from './AppEditBuyerAddressModal';

interface VerifyJobRequestData {
  zipCode: string;
  state: string;
  city: string;
  address: string;
  buyerId: string;
  jobNumber: string;
  name: string;
  buyerName: string;
}

type PartialSupplierJobPreVerify = Pick<
  SupplierJobUpdate,
  | 'buyerId'
  | 'name'
  | 'jobNumber'
  | 'jobAddress'
  | 'jobCity'
  | 'jobState'
  | 'jobZipCode'
  | 'constructionMonitorPermitId'
>;

type DecodedData = {
  expiresAt: number;
  supplierId: string;
  domainId: string;
  jobId: string;
  name: string;
  jobNumber: string;
  jobAddress: string;
  jobCity: string;
  jobState: string;
  jobZipCode: string;
  owners: SupplierJobAddressDetails[];
  GCs: SupplierJobAddressDetails[];
  buyerId: string;
  buyerName: string;
  lenders: SupplierJobAddressDetails[];
  apn: string;
  estimatedMaterialAmount: number;
  jobType: string;
  jobSector: string;
  customerType: string;
  jobFurnishings: string;
  iat: number;
  buyerAddress?: string;
  buyerCity?: string;
  buyerState?: string;
  buyerZipCode?: string;
};

enum ModalState {
  ADD_JOB,
  VERIFY_JOB,
  SUCCESS_SAVING_JOB,
}

const INITIAL_STATE = {
  name: '',
  jobNumber: '',
  jobAddress: '',
  jobCity: '',
  jobState: '',
  jobZipCode: '',
  buyerId: '',
  constructionMonitorPermitId: null,
} as PartialSupplierJobPreVerify;
const RequestJobInformationSheet = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { basicSupplier } = useAppSelector((state) => state.buyers);

  const [supplierJobInput, setSupplierJobInput] = useState(INITIAL_STATE);
  const [showErrorAbove, setShowErrorAbove] = useState(false);
  const [errors, setErrors] = useState(INITIAL_STATE);
  const [decodedData, setDecodedData] = useState<DecodedData | null>(null);
  const [domainId, setDomainId] = useState('');
  const [modalState, setModalState] = useState(ModalState.ADD_JOB);
  const [verifying, setVerifying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [verifyJobData, setVerifyJobData] = useState<SupplierVerifiedJobFormData>(
    {} as SupplierVerifiedJobFormData
  );
  const [modalTitle, setModalTitle] = useState('');
  const [modalDetails, setModalDetail] = useState('');
  const [modalType, setModalType] = useState<'Success' | 'Error'>('Success');
  const [submitEmailModal, setSubmitEmailModal] = useState(false);
  const [submitData, setSubmitData] = useState<RequestedSupplierJobCreate>(
    {} as RequestedSupplierJobCreate
  );
  const [attachments, setAttachments] = useState<File[]>([]);
  const [openEditBuyerAddress, setOpenEditBuyerAddress] = useState(false);
  const [buyerAddress, setBuyerAddress] = useState(
    {} as {
      address: string;
      city: string;
      state: string;
      zipCode: string;
    }
  );

  const fetchDataUsingDomainId = async (supplierDomainId: string) => {
    try {
      const response = await dispatch(getBasicSupplier({ supplierDomainId }));
      if (response.type === 'buyers/getBasicSupplier/fulfilled') {
        const supplier: Supplier = (response.payload as { data: Supplier })?.data;
        if (supplier) {
          setDecodedData({
            supplierId: supplier.id,
            domainId: supplier.domainId,
            jobId: '',
            name: '',
            jobNumber: '',
            jobAddress: '',
            jobCity: '',
            jobState: '',
            jobZipCode: '',
            owners: [],
            GCs: [],
            buyerId: '',
            buyerName: '',
            lenders: [
              {
                name: '',
                address: '',
                city: '',
                state: '',
                zipCode: '',
              },
            ],
            apn: '',
            estimatedMaterialAmount: 0,
            jobType: 'commercial',
            jobSector: 'private',
            customerType: 'SUB',
            jobFurnishings: '',
            iat: 0,
            expiresAt: 0,
          });
          setModalState(ModalState.ADD_JOB);
        } else {
          setModalState(ModalState.SUCCESS_SAVING_JOB);
          setModalType('Error');
          setModalTitle('Invalid link!');
          setModalDetail('The link you are trying to access is invalid');
        }
      }
    } catch (error) {
      setModalState(ModalState.SUCCESS_SAVING_JOB);
      setModalType('Error');
      setModalTitle('Invalid link!');
      setModalDetail('The link you are trying to access is invalid');
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const paramDomainId = params.get('domainId');
    if (paramDomainId) {
      fetchDataUsingDomainId(paramDomainId);
    } else if (token) {
      try {
        const decoded: DecodedData = jwtDecode(token);
        setDecodedData(decoded);
        const supplierDomainId = decoded?.domainId ?? '';
        setDomainId(supplierDomainId);
        setModalState(ModalState.ADD_JOB);
        setBuyerAddress({
          address: decoded?.buyerAddress ?? '',
          city: decoded?.buyerCity ?? '',
          state: decoded?.buyerState ?? '',
          zipCode: decoded?.buyerZipCode ?? '',
        });
      } catch (error) {
        setModalState(ModalState.SUCCESS_SAVING_JOB);
        setModalType('Error');
        setModalTitle('Invalid link!');
        setModalDetail('The link you are trying to access is invalid');
      }
    } else {
      setModalState(ModalState.SUCCESS_SAVING_JOB);
      setModalType('Error');
      setModalTitle('Invalid link!');
      setModalDetail('The link you are trying to access is invalid');
      return;
    }
  }, []);

  useEffect(() => {
    if (domainId) {
      dispatch(getBasicSupplier({ supplierDomainId: domainId }));
    }
  }, [domainId]);

  const onVerifyJobState = (dataToVerify: SupplierVerifiedJobFormData) => {
    setVerifyJobData(dataToVerify);
    if (decodedData) {
      const newData = {
        ...decodedData,
        owners: dataToVerify.owners ?? [],
        GCs: dataToVerify.GCs ?? decodedData.GCs,
        apn: dataToVerify.apn ?? decodedData.apn,
        constructionMonitorPermitId: dataToVerify.constructionMonitorPermitId ?? null,
        jobType: dataToVerify.jobType ?? decodedData.jobType,
        buyerName:
          decodedData.buyerName === '' ? dataToVerify.buyerName ?? '' : decodedData.buyerName ?? '',
        lenders: [
          {
            name: '',
            address: '',
            city: '',
            state: '',
            zipCode: '',
          },
        ],
      };
      setDecodedData(newData);
    }
    setModalState(ModalState.VERIFY_JOB);
  };

  const handleVerifyData = async (jobAddressData: VerifyJobRequestData) => {
    // setVerifying(true);
    // if (basicSupplier?.ownerVerificationEnabled) {
    //   await dispatch(fetchQueuryJobSiteAddresses(jobAddressData)).then((res) => {
    //     const { address, city, state, zipCode, jobNumber, name, buyerId } = jobAddressData;
    //     if (res.type === 'jobOwner/fetchAddresses/fulfilled') {
    //       const permits = res.payload as ContructionMonitorPermitOption[];

    //       const queriedJobAddress = `${address}, ${city}, ${state}, ${zipCode}`;

    //       if (permits.length > 0) {
    //         // loop through results and use compareTwoStrings to queriedJobAddress to find the best match
    //         // best match will be the one with a score above 0.5 or null if none are above 0.5
    //         let bestMatch: ContructionMonitorPermitOption | null = null;
    //         let bestMatchScore = 0;

    //         for (const permit of permits) {
    //           const permitAddress = `${permit.ownerAddress}, ${permit.ownerCity}, ${permit.ownerState}, ${permit.ownerZipCode}`;
    //           const score = stringSimilarity.compareTwoStrings(queriedJobAddress, permitAddress);

    //           if (score > 0.5 && score > bestMatchScore) {
    //             bestMatch = permit;
    //             bestMatchScore = score;
    //           }
    //           const jobToVerify = {
    //             name,
    //             buyerId,
    //             owner: '',
    //             ownerAddress: '',
    //             ownerCity: '',
    //             ownerState: '',
    //             ownerZipCode: '',
    //             jobNumber,
    //             jobAddress: address,
    //             jobCity: city,
    //             jobState: state,
    //             jobZipCode: zipCode,
    //             constructionMonitorPermitId: null,
    //             GC: '',
    //             GCAddress: '',
    //             GCCity: '',
    //             GCState: '',
    //             GCZipCode: '',
    //             apn: '',
    //             customerType: 'SUB' as const,
    //           };

    //           if (bestMatch) {
    //             onVerifyJobState({
    //               ...jobToVerify,
    //               owner: bestMatch.owner,
    //               ownerAddress: bestMatch.ownerAddress,
    //               ownerCity: bestMatch.ownerCity,
    //               ownerState: bestMatch.ownerState,
    //               ownerZipCode: bestMatch.ownerZipCode,
    //               constructionMonitorPermitId: Number(bestMatch.constructionMonitorPermitId),
    //               GC: bestMatch.gcName,
    //               GCAddress: bestMatch.gcAddress,
    //               GCCity: bestMatch.gcCity,
    //               GCState: bestMatch.gcState,
    //               GCZipCode: bestMatch.gcZipCode,
    //               jobType: bestMatch.jobType,
    //               apn: bestMatch.apn,
    //               verifiedProperty: {
    //                 address: bestMatch.jobAddress,
    //                 city: bestMatch.jobCity,
    //                 state: bestMatch.jobState,
    //                 zipCode: bestMatch.jobZipCode,
    //                 apn: bestMatch.apn,
    //               },
    //             });
    //             setVerifying(false);
    //           } else {
    //             onVerifyJobState(jobToVerify);
    //             setVerifying(false);
    //           }
    //         }
    //       }
    //     } else {
    //       console.error('Error fetching data: ', res);
    //       setVerifying(false);
    //     }
    //   });
    // } else {
    const { address, city, state, zipCode, jobNumber, name, buyerId, buyerName } = jobAddressData;
    const jobToVerify = {
      name,
      buyerId,
      owners: [
        {
          name: '',
          address: '',
          city: '',
          state: '',
          zipCode: '',
        },
      ],
      jobNumber,
      jobAddress: address,
      jobCity: city,
      jobState: state,
      jobZipCode: zipCode,
      constructionMonitorPermitId: null,
      GCs: [
        {
          name: '',
          address: '',
          city: '',
          state: '',
          zipCode: '',
        },
      ],
      apn: '',
      jobType: 'commercial' as const,
      jobSector: 'private' as const,
      customerType: 'SUB' as const,
      jobFurnishings: '',
      buyerName,
    };
    onVerifyJobState(jobToVerify);
    setVerifying(false);
    // }
  };

  const handleSubmitJob = async (formData: SupplierVerifiedJobFormData, files?: File[]) => {
    setSubmitEmailModal(true);
    if (files) {
      setAttachments(files);
    }
    let moreDetails = {};
    if (formData.jobState === 'AZ') {
      moreDetails = {
        jobCounty: formData.jobCounty ?? '',
        jobLegalDescription: formData.jobLegalDescription ?? '',
      };
    }
    if (!formData.hasNoLender) {
      moreDetails = {
        ...moreDetails,
        lenders: formData.lenders || [],
      };
    } else {
      moreDetails = {
        ...moreDetails,
        lenders: [],
      };
    }
    if (formData.isJobBonded) {
      moreDetails = {
        ...moreDetails,
        sureties: formData.sureties || [],
      };
    } else {
      moreDetails = {
        ...moreDetails,
        sureties: [],
      };
    }
    const data = {
      supplierId: decodedData?.supplierId || '',
      supplierJobId: decodedData?.jobId || '',
      name: formData.name || '',
      jobNumber: formData.jobNumber || '',
      jobAddress: formData.jobAddress || '',
      jobCity: formData.jobCity || '',
      jobState: formData.jobState || '',
      jobZipCode: formData.jobZipCode || '',
      owners: formData.owners || [],
      buyerId: decodedData?.buyerId || '',
      GCs: formData.GCs || [],
      apn: formData.apn || '',
      constructionMonitorPermitId: Number(formData.constructionMonitorPermitId) || null,
      estimatedMaterialAmount: formData.estimatedMaterialAmount
        ? Number(formData.estimatedMaterialAmount)
        : null,
      jobType: formData.jobType || 'commercial',
      jobSector: formData.jobSector || 'private',
      customerType: formData.customerType || 'SUB',
      jobFurnishings: formData.jobFurnishings || '',
      hasNoLender: formData.hasNoLender || false,
      isJobBonded: formData.isJobBonded || false,
      status: 'pending' as const,
      buyerName: decodedData?.buyerName || '',
      ...moreDetails,
    };
    setSubmitData(data);
  };

  const handleSubmitData = async (email: string) => {
    setSaving(true);
    const ccEmail = [
      ...new Set([
        'team@usedill.com',
        basicSupplier?.email ?? '',
        ...(basicSupplier?.prelienNotificationEmails || []),
      ]),
    ];
    let additionalData = {};

    if (decodedData?.buyerAddress) {
      const shouldUpdateCustomerAddress =
        decodedData?.buyerAddress !== buyerAddress?.address ||
        decodedData?.buyerCity !== buyerAddress?.city ||
        decodedData?.buyerState !== buyerAddress?.state ||
        decodedData?.buyerZipCode !== buyerAddress?.zipCode;

      if (
        shouldUpdateCustomerAddress &&
        buyerAddress?.address &&
        buyerAddress?.city &&
        buyerAddress?.state &&
        buyerAddress?.zipCode
      ) {
        additionalData = {
          customerAddress: {
            address: buyerAddress.address,
            city: buyerAddress.city,
            state: buyerAddress.state,
            zipCode: buyerAddress.zipCode,
          },
        };
      }
    }

    const createdRequestedSupplierJob = await dispatch(
      createRequestedSupplierJob({
        requestedSupplierJobDetails: {
          ...submitData,
          ...additionalData,
        },
        emailTo: email,
        ccEmails: ccEmail.filter((eml) => eml !== '' && eml !== email),
        files: attachments,
      })
    );
    if (
      createdRequestedSupplierJob.type ===
      'requestJobInformation/createRequestedSupplierJob/fulfilled'
    ) {
      setModalState(ModalState.SUCCESS_SAVING_JOB);
      setSaving(false);
      setModalType('Success');
      setModalTitle('Success!');
      setModalDetail(
        `The job information has been successfully sent to ${basicSupplier?.name ?? ''}.`
      );
    } else {
      setModalState(ModalState.SUCCESS_SAVING_JOB);
      setModalType('Error');
      setModalTitle('Error!');
      setModalDetail(
        `There was an error sending the job information to ${
          basicSupplier?.name ?? ''
        }. Please try again later.`
      );
      setSaving(false);
    }
  };
  const submitBuyerAddress = async (address: {
    address: string;
    city: string;
    state: string;
    zipCode: string;
  }) => {
    setBuyerAddress(address);
  };

  return (
    <div className="w-full GREY_100-BG min-h-full pb-4">
      <AppSubmitEmailModal
        open={submitEmailModal}
        handleClose={() => setSubmitEmailModal(false)}
        onSubmit={handleSubmitData}
        title="Are you sure you would like to submit this job information sheet?"
      />
      <AppEditBuyerAddressModal
        open={openEditBuyerAddress}
        handleClose={() => setOpenEditBuyerAddress(false)}
        onSubmit={submitBuyerAddress}
        address={{
          address: decodedData?.buyerAddress ?? '',
          city: decodedData?.buyerCity ?? '',
          state: decodedData?.buyerState ?? '',
          zipCode: decodedData?.buyerZipCode ?? '',
        }}
      />
      <div className="w-full flex items-center justify-center text-xl WHITE-BG font-bold  p-5">
        Job Information Sheet
      </div>
      <div className="flex flex-col px-5 py-4 w-full">
        {basicSupplier && basicSupplier.logo && basicSupplier.logo?.url && (
          <div className="flex flex-col items-center mb-4">
            <img className="h-[80px]" src={basicSupplier.logo.url} />
            <div className="flex items-center mt-2">
              <p>Powered By</p>
              <img className="h-8 ml-2" src={dillLogoWithText} />
            </div>
          </div>
        )}
        {(!basicSupplier || (basicSupplier && !basicSupplier.logo?.url)) && (
          <div className="flex flex-col items-center mb-4 w-full">
            <img className="w-[200px]" src={dillLogoWithText} />
          </div>
        )}
        <div className="flex justify-center items-center">
          <div className="w-1/3">
            <div className="text-center font-semibold">
              Hi <span className="capitalize">{`${decodedData?.buyerName ?? ''}`}</span>, you’ve
              been invited by{' '}
              <span className="PRIMARY_500-CLR">{`${basicSupplier?.name ?? ''}`}</span>, to complete
              the job information below.
            </div>
            {decodedData?.buyerAddress !== undefined &&
              modalState !== ModalState.SUCCESS_SAVING_JOB && (
                <div className="flex justify-between items-center my-2 PRIMARY_100-BG w-full p-2  rounded-lg">
                  <div>
                    <div className="font-semibold text-sm">Your Address</div>
                    <div className="text-sm">{`${
                      buyerAddress?.address ? `${buyerAddress.address},` : ''
                    } ${buyerAddress?.city ? `${buyerAddress?.city},` : ''} ${
                      buyerAddress?.state ?? ''
                    } ${buyerAddress?.zipCode ?? ''}`}</div>
                  </div>
                  <AppButton
                    text="Edit"
                    onClick={() => setOpenEditBuyerAddress(true)}
                    buttonStyles={{
                      marginTop: '4px',
                      marginBottom: '4px',
                      width: '100px',
                      height: '35px',
                    }}
                  />
                </div>
              )}
            {modalState === ModalState.ADD_JOB && decodedData && (
              <JobDetailsForm
                onFormSubmit={handleVerifyData}
                jobSiteData={{
                  name: verifyJobData?.name || decodedData?.name || '',
                  jobNumber: verifyJobData?.jobNumber || decodedData?.jobNumber || '',
                  jobAddress: verifyJobData?.jobAddress || decodedData?.jobAddress || '',
                  jobCity: verifyJobData?.jobCity || decodedData?.jobCity || '',
                  jobState: verifyJobData?.jobState || decodedData?.jobState || '',
                  jobZipCode: verifyJobData?.jobZipCode || decodedData?.jobZipCode || '',
                  buyerId: verifyJobData?.buyerId || decodedData?.buyerId || '',
                  constructionMonitorPermitId: verifyJobData?.constructionMonitorPermitId || null,
                  buyerName: decodedData?.buyerName || '',
                }}
                verifying={verifying}
              />
            )}
            {modalState === ModalState.VERIFY_JOB && (
              <VerifyJobForm
                {...verifyJobData}
                supplierJobData={supplierJobInput}
                onUseVerifiedData={handleSubmitJob}
                onManualDataUpdate={handleSubmitJob}
                onGoBack={() => setModalState(ModalState.ADD_JOB)}
                saving={saving}
                ownerVerificationEnable={false}
                // ownerVerificationEnable={basicSupplier?.ownerVerificationEnabled}
              />
            )}
            {modalState === ModalState.SUCCESS_SAVING_JOB && (
              <JobSavingSuccess type={modalType} details={modalDetails} title={modalTitle} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestJobInformationSheet;
